// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { DisplayUnit, toDisplayValue, fromDisplayValue } from './cadUnits';

/**
 * The user's own vertical nudge (metres) for a geodatabase — what the sidebar's
 * "Elevation offset" field holds, always in metres regardless of its display-unit toggle.
 *
 * This is a DELTA on top of the order's automatic geoid correction, not the total shift, so 0
 * means "sit on the geoid plane" — the geodetically correct position — rather than "no correction
 * at all". Storing the delta also means a geoid correction backfilled onto the order later still
 * takes effect, instead of being overridden by a stale total.
 * @returns the user's nudge in metres; 0 when they have never set one.
 */
export const resolveUserElevationOffset = (storedOffsetZ: number | null | undefined): number => {
  if (typeof storedOffsetZ === 'number' && Number.isFinite(storedOffsetZ)) return storedOffsetZ;
  return 0;
};

/**
 * The automatic geoid correction (metres) for the SELECTED order.
 *
 * Deliberately matched by id: `state.order.list` is not project-filtered, so picking "the first
 * order that happens to carry a value" could apply a neighbouring order's correction — or none at
 * all while a sibling has one. Both were happening before this was matched properly.
 * @returns the selected order's correction, or 0 when it has none.
 */
export const resolveOrderGeoidOffset = (
  orders: { _id: string; geoidOffsetZ?: number }[] | undefined | null,
  selectedOrderId: string | null | undefined,
): number => {
  if (!Array.isArray(orders) || !selectedOrderId) return 0;
  const order = orders.find((candidate) => candidate._id === selectedOrderId);
  const offset = order?.geoidOffsetZ;
  return typeof offset === 'number' && Number.isFinite(offset) ? offset : 0;
};

/**
 * Whether a stored automatic offset is a value the viewer can act on. Exactly 0 is the placeholder
 * an order carries before any lookup has produced a value: the explicit default the sweeps wrote,
 * and the Mongoose default once AUT-6422 lands. The geoid separation is never 0 anywhere we work
 * (the smallest real value ever stored is 17 m), so 0 always means "none was calculated", never
 * "the geoid is flat here".
 * @returns true for a finite, non-zero number.
 */
export const hasUsableGeoidOffset = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value !== 0;

/**
 * What a draft (unplaced) order can say about its automatic offset. Display only: nothing is
 * applied until the order is placed, so this never feeds the ground offset.
 * - 'no-kml': the draft holds no KML right now, so no lookup has run for it. Keyed on the KML the
 *   draft holds at this moment (deleting one clears boundaryFile and the server nulls the offset),
 *   so add, remove, add again reads correctly whatever placeholder the value falls back to.
 * - 'none': a KML is there but no usable value came of it: the lookup failed at upload, a corridor
 *   is not buffered yet, or the value is the 0 placeholder.
 * - 'ready': a real value, waiting for placement.
 */
export type PendingGeoidState =
  | { kind: 'no-kml' }
  | { kind: 'none' }
  | { kind: 'ready'; offset: number };

export const resolvePendingGeoidState = (
  draftOrder: { boundaryFile?: string | null; geoidOffsetZ?: number | null } | undefined | null,
): PendingGeoidState => {
  if (!draftOrder?.boundaryFile) return { kind: 'no-kml' };
  const offset = draftOrder.geoidOffsetZ;
  return hasUsableGeoidOffset(offset) ? { kind: 'ready', offset } : { kind: 'none' };
};

/**
 * How the viewer should read a placed order's stored offset.
 * - 'resolved': a usable value, applied to the ground.
 * - 'none': exactly 0, the placeholder for "none was calculated" (a swept order, the default once
 *   AUT-6422 lands, or a deliberate zero). Shown as having no automatic offset and never sent to
 *   the server: a stored 0 is intentional and must not be recomputed over.
 * - 'ask-server': no value at all, worth one lookup.
 */
export type PlacedGeoidResolution = 'resolved' | 'none' | 'ask-server';

export const resolvePlacedGeoidResolution = (stored: unknown): PlacedGeoidResolution => {
  if (hasUsableGeoidOffset(stored)) return 'resolved';
  if (stored === 0) return 'none';
  return 'ask-server';
};

/**
 * The total vertical shift in force (metres) — the automatic geoid correction plus the user's nudge.
 * This is what the sidebar reports as "Applied", so a 0 in the input cannot be read as "nothing
 * happened" when a correction is doing the work.
 * @returns the applied offset in metres.
 */
export const resolveAppliedOffset = (projectGeoidOffset: number, userElevationOffset: number): number => {
  const geoid = Number.isFinite(projectGeoidOffset) ? projectGeoidOffset : 0;
  const user = Number.isFinite(userElevationOffset) ? userElevationOffset : 0;
  return geoid + user;
};

/**
 * The shift to apply to the ground (see OffsetElevationLayer). Lowering the terrain by the applied
 * offset lifts every native-Z data layer by the same amount at once.
 * @returns the ground offset in metres.
 */
export const resolveGroundOffset = (projectGeoidOffset: number, userElevationOffset: number): number =>
  -resolveAppliedOffset(projectGeoidOffset, userElevationOffset);

/**
 * What the elevation-offset input should show for a stored metres value in the given display
 * unit. Feet is a derived conversion of a metres-native value, so it rarely lands on a clean
 * decimal — rounded to 2dp for display. Metres is rounded too, but only to 6dp: far finer than
 * anyone would type by hand, so a genuinely-typed value is untouched, but it strips the floating-
 * point noise a value picks up from a metres->feet->metres round trip (e.g. a value entered as
 * feet and later viewed in metres) — left unrounded, that noise would display verbatim as
 * something like "4.998720000000001" instead of "4.99872".
 * Trailing zeros are dropped in both units, so 0 reads as "0" whichever unit is selected.
 * @returns the string to put in the input.
 */
export const formatOffsetDraft = (valueMetres: number, unit: DisplayUnit, feetPerMetre: number): string => {
  const displayValue = toDisplayValue(valueMetres, unit, feetPerMetre);
  if (unit === 'ft') return String(Number(displayValue.toFixed(2)));
  return String(Number(displayValue.toFixed(6)));
};

/**
 * Parses what the reader typed into the elevation-offset input, in the given display unit, back
 * to metres for storage. An empty field or a lone "-" (mid-typing a negative number) is treated as
 * an explicit 0, matching the field's own placeholder semantics rather than being rejected.
 * @returns the value in metres, or null when the typed text isn't a number at all.
 */
export const parseOffsetDraftToMetres = (draft: string, unit: DisplayUnit, feetPerMetre: number): number | null => {
  const trimmed = draft.trim();
  const displayValue = trimmed === '' || trimmed === '-' ? 0 : Number(trimmed);
  if (!Number.isFinite(displayValue)) return null;
  return fromDisplayValue(displayValue, unit, feetPerMetre);
};
